"use client";
import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const faqs = [
  {
    question: "What is Salarymates?",
    answer:
      "Salarymates is an instant fund service designed for salaried individuals who need quick financial assistance with minimal documentation.",
    icon: "💰",
  },
  {
    question: "How quickly can I get funds?",
    answer:
      "Fund approvals are processed within minutes and the amount is often credited the same day.",
    icon: "⚡",
  },
  {
    question: "What documents are required?",
    answer:
      "A valid ID proof, latest salary slip, and bank statement of your salary account.",
    icon: "📄",
  },
  {
    question: "Who is eligible?",
    answer:
      "Salaried individuals aged 21–58 with stable income and an active salary account.",
    icon: "👤",
  },
  {
    question: "Is the process fully online?",
    answer:
      "Yes, the entire process is completely digital — no physical paperwork needed.",
    icon: "📱",
  },
  // {
  //   question: "What are the charges?",
  //   answer:
  //     "Our charges range from 0.25% to 1% per day depending on your profile and fund requirement.",
  //   icon: "📊",
  // },
  {
    question: "Maximum fund amount?",
    answer: "You can get instant funds between ₹5,000 and ₹100,000.",
    icon: "💎",
  },
  {
    question: "Is my data safe?",
    answer:
      "Yes, all your data is protected with industry-grade encryption and never shared.",
    icon: "🔒",
  },
];

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section className="relative bg-[#0A1620] overflow-hidden py-20 lg:py-24">
      {/* Dark Theme Background */}
      <div className="absolute inset-0 bg-[#0A1620]"></div>

      {/* Premium Accent Gradients */}
      <div className="absolute top-40 right-0 w-[500px] h-[500px] bg-[#1B4D5E]/20 rounded-full blur-[120px]"></div>
      <div className="absolute bottom-40 left-0 w-[400px] h-[400px] bg-[#1A3B4D]/20 rounded-full blur-[100px]"></div>
      
      {/* Subtle Grid Pattern */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(to right, #2D5F7A 1px, transparent 1px), linear-gradient(to bottom, #2D5F7A 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
          }}
        ></div>
      </div>

      {/* Top Wave */}
      <svg
        className="absolute top-0 left-0 w-full h-48 rotate-180"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 1440 320"
        preserveAspectRatio="none"
      >
        <path
          fill="#2D5F7A"
          fillOpacity="0.08"
          d="M0,192L48,176C96,160,192,128,288,128C384,128,480,160,576,176C672,192,768,192,864,176C960,160,1056,128,1152,128C1248,128,1344,160,1392,176L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
        ></path>
      </svg>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ width: 0 }}
            whileInView={{ width: "80px" }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="h-0.5 bg-gradient-to-r from-[#7BC4E0] to-transparent mx-auto mb-6"
          />
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-[#7BC4E0] font-mono text-sm font-semibold tracking-[0.3em] uppercase mb-4 block"
          >
            Got Questions?
          </motion.span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            Frequently Asked{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#7BC4E0] to-[#4D9ABF]">
              Questions
            </span>
          </h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-base md:text-lg text-[#CCE6F5] max-w-2xl mx-auto"
          >
            Find answers to common questions about our instant fund services
          </motion.p>
        </motion.div>

        {/* FAQ List */}
        <div className="flex flex-col gap-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="relative"
            >
              {/* Question Card */}
              <div className="flex items-start gap-3">
                {/* Icon */}
                <div className="flex-shrink-0 mt-2">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#1A2F3A] to-[#0F2128] rounded-xl border border-[#2D5F7A]/30 flex items-center justify-center">
                    <span className="text-lg text-[#7BC4E0]">{faq.icon}</span>
                  </div>
                </div>

                {/* Question Button */}
                <button
                  onClick={() =>
                    setOpenIndex(openIndex === index ? null : index)
                  }
                  className={`flex-1 flex justify-between items-center text-left p-5 
                    bg-gradient-to-br from-[#1A2F3A] to-[#0F2128]
                    border rounded-xl
                    transition-all duration-300
                    group
                    ${
                      openIndex === index
                        ? "border-[#7BC4E0] shadow-lg shadow-[#7BC4E0]/10"
                        : "border-[#2D5F7A]/30 hover:border-[#7BC4E0]/50"
                    }`}
                >
                  <span className="text-sm sm:text-base md:text-lg font-medium text-white pr-4">
                    {faq.question}
                  </span>

                  <span
                    className={`text-2xl font-light text-[#7BC4E0] transform transition-all duration-500 flex-shrink-0
                      ${openIndex === index ? "rotate-45" : "group-hover:scale-125"}`}
                  >
                    +
                  </span>
                </button>
              </div>

              {/* Answer Box */}
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{
                  height: openIndex === index ? "auto" : 0,
                  opacity: openIndex === index ? 1 : 0,
                }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="overflow-hidden"
              >
                <div className="mt-2 ml-13 pl-13">
                  <div className="bg-gradient-to-br from-[#1A2F3A]/50 to-[#0F2128]/50 rounded-xl p-5 border border-[#2D5F7A]/30 ml-12">
                    <p className="text-[#CCE6F5] text-sm md:text-base leading-relaxed border-l-2 border-[#7BC4E0] pl-4">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Support CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-16"
        >
          <div className="bg-gradient-to-br from-[#1A2F3A] to-[#0F2128] rounded-2xl p-8 border border-[#2D5F7A]/30">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-[#2D5F7A] to-[#1E3B4A] rounded-full flex items-center justify-center border border-[#7BC4E0]/30">
                  <span className="text-2xl text-[#7BC4E0]">💬</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">
                    Still have questions?
                  </h3>
                  <p className="text-[#8FAFC7]">We're here to help 24/7</p>
                </div>
              </div>
              <Link
                to="/contact"
                className="px-8 py-3 bg-gradient-to-r from-[#2D5F7A] to-[#1E3B4A] text-white rounded-full font-semibold hover:from-[#3A708F] hover:to-[#2A4A5F] transition-all duration-300 shadow-lg border border-[#7BC4E0]/20 hover:-translate-y-1"
              >
                Contact Support
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Trust Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="flex justify-center items-center gap-6 mt-8"
        >
          <span className="text-sm text-[#8FAFC7]">
            Trusted by salaried professionals across India
          </span>
        </motion.div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden pointer-events-none">
        <svg
          className="relative block w-full h-16 md:h-20"
          viewBox="0 0 1440 120"
          preserveAspectRatio="none"
        >
          <path
            fill="#2D5F7A"
            fillOpacity="0.08"
            d="M0,64 C240,96 480,32 720,32 C960,32 1200,96 1440,64 L1440,120 L0,120 Z"
          />
        </svg>
      </div>
    </section>
  );
};

export default FAQSection;